import { memo } from 'react';
import { Handle, Position, NodeProps, type Node } from '@xyflow/react';
import { CommandLineIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { DefinitionNodeData } from '../../../utils/definitionTransform';
import { FunctionNode as FunctionNodeType } from '../../../types/definition';
import { ValidationError } from '../../../utils/definitionValidation';

interface ExtendedNodeData extends DefinitionNodeData {
  validationErrors?: ValidationError[];
}

function FunctionNodeComponent({ data, selected }: NodeProps<Node<ExtendedNodeData>>) {
  const node = data.originalNode as FunctionNodeType;
  const errors = data.validationErrors?.filter(e => e.severity === 'error') || [];
  const warnings = data.validationErrors?.filter(e => e.severity === 'warning') || [];
  const hasErrors = errors.length > 0;
  const hasWarnings = warnings.length > 0;
  const hasOutput = node.output && Object.keys(node.output).length > 0;

  const getBorderColor = () => {
    if (hasErrors) return 'border-red-500';
    if (selected) return 'border-purple-500 ring-2 ring-purple-200 dark:ring-purple-800';
    return 'border-purple-400 dark:border-purple-600';
  };

  return (
    <div
      className={`
        px-4 py-3 rounded-lg border-2 bg-white dark:bg-gray-800 shadow-sm min-w-[160px]
        ${getBorderColor()}
      `}
    >
      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Left}
        className="!w-3 !h-3 !bg-purple-500 !border-2 !border-white dark:!border-gray-800"
      />

      <div className="flex items-center gap-2">
        <div className={`p-1.5 rounded-md ${hasErrors ? 'bg-red-100 dark:bg-red-900' : 'bg-purple-100 dark:bg-purple-900'}`}>
          <CommandLineIcon className={`w-4 h-4 ${hasErrors ? 'text-red-600 dark:text-red-400' : 'text-purple-600 dark:text-purple-400'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate flex items-center gap-1">
            {data.label}
            {(hasErrors || hasWarnings) && (
              <ExclamationCircleIcon className={`w-4 h-4 ${hasErrors ? 'text-red-500' : 'text-amber-500'}`} />
            )}
          </div>
          <div className="text-xs text-purple-600 dark:text-purple-400 font-mono">
            {node.function ? `${node.function}()` : 'No function'}
          </div>
        </div>
      </div>

      <div className="mt-1.5 flex items-center gap-1 flex-wrap">
        {node.entry && (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300">
            Entry
          </span>
        )}
        {hasOutput && (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300">
            Exit
          </span>
        )}
      </div>

      {hasErrors && (
        <div className="mt-2 text-xs text-red-600 dark:text-red-400 truncate">
          {errors[0].message}
        </div>
      )}

      {/* Output handle */}
      <Handle
        type="source"
        position={Position.Right}
        className="!w-3 !h-3 !bg-purple-500 !border-2 !border-white dark:!border-gray-800"
      />
    </div>
  );
}

export const FunctionNode = memo(FunctionNodeComponent);
